import { cva, type VariantProps } from 'class-variance-authority'
import { type HTMLAttributes, type RefObject } from 'react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive: 'border-destructive text-destructive [&>svg]:text-destructive',
        info: 'border-border bg-secondary text-muted-foreground [&>svg]:text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const Alert = ({
  className,
  variant,
  ref,
  ...props
}: (HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>) & {
  ref?: RefObject<HTMLDivElement | null>
}) => <div className={cn(alertVariants({ variant }), className)} ref={ref} role="alert" {...props} />
Alert.displayName = 'Alert'

const AlertTitle = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { ref?: RefObject<HTMLParagraphElement | null> }) => (
  <h5 className={cn('mb-1 font-medium leading-none tracking-tight', className)} ref={ref} {...props} />
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { ref?: RefObject<HTMLParagraphElement | null> }) => (
  <div className={cn('text-sm [&_p]:leading-relaxed', className)} ref={ref} {...props} />
)
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertDescription, AlertTitle }
